'use client'

import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { useMeasure } from 'react-use'
import { Button } from '@/components/button'
import { Gradient } from '@/components/gradient'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { IChildrenProps } from '@/typings/children-props'
import { noOp } from '@/utils/functions/noOp'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { useMobile } from '@/utils/window/use-mobile'

type IInlinePaginationMakeItemRef = (
    value: string,
    isLast: boolean
) => (element: HTMLDivElement) => void

export type IInlinePaginationScrollToRef = (value: string) => void

interface IInlinePaginationProps extends IChildrenProps {
    optionsCount: number | undefined
    buttonSize?: string
    spacing?: number
    className?: string
    renderChildren?: (
        makeItemRef: IInlinePaginationMakeItemRef,
        scrollToRef: IInlinePaginationScrollToRef
    ) => ReactNode
}

export const InlinePagination = ({
    children,
    optionsCount,
    buttonSize = '40px',
    spacing = 12,
    className,
    renderChildren,
}: IInlinePaginationProps) => {
    const itemRefs = useRef<Record<string, HTMLDivElement>>({})

    const [withButtons, setWithButtons] = useState(false)

    const isMobile = useMobile()

    const hasSynced = useRef(false)
    useEffect(() => {
        if (hasSynced.current || typeof window === 'undefined' || isMobile !== false) return

        const isMacbook = window.navigator.platform.includes('Mac')

        setWithButtons(!isMacbook)
    }, [isMobile])

    const [containerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>()
    const containerScrollRef = useRef<HTMLDivElement>(null)

    const [page, setPage] = useState(1)
    const [pagesCount, setPagesCount] = useState(1)

    useLayoutEffect(() => {
        if (!containerScrollRef.current) return

        containerScrollRef.current.scrollTo({
            left: containerWidth * (page - 1),
            behavior: 'smooth',
        })
    }, [page, containerWidth])

    const calculatePagesCount = useCallback(() => {
        if (!containerWidth || optionsCount === undefined) return

        const numberOfTagRefs = Object.keys(itemRefs.current).length

        if (numberOfTagRefs !== optionsCount) return

        const tagsWidth = Object.values(itemRefs.current).reduce((acc, tag, index, arr) => {
            const isLast = index === arr.length - 1

            return acc + tag?.offsetWidth + (isLast ? 0 : spacing)
        }, 0)

        const tagsPagesCount = Math.ceil(tagsWidth / containerWidth)

        if (Number.isNaN(tagsPagesCount)) return

        setPagesCount(tagsPagesCount)
    }, [containerWidth, optionsCount, spacing])

    useLayoutEffect(() => {
        calculatePagesCount()
    }, [calculatePagesCount])

    const makeItemRef = useCallback<IInlinePaginationMakeItemRef>(
        (value, isLast) => (element) => {
            itemRefs.current[value] = element

            if (isLast) {
                calculatePagesCount()
            }
        },
        [calculatePagesCount]
    )

    const scrollToRef = useCallback<IInlinePaginationScrollToRef>(
        (value) => {
            const element = itemRefs.current[value]

            if (!element || !containerScrollRef.current) return

            const overflowingWidth = element.offsetLeft + element.offsetWidth - containerWidth

            containerScrollRef.current.scrollTo({
                left: containerScrollRef.current.scrollLeft + overflowingWidth,
                behavior: 'smooth',
            })
        },
        [containerWidth]
    )

    return (
        <div
            className={cn(
                'flex flex-row w-full gap-x-4 justify-between scrollbar-hide items-center relative',
                className
            )}
        >
            {withButtons && (
                <Button
                    shape="circle"
                    styleType="tertiary"
                    disabled={page === 1}
                    onClick={() => {
                        setPage((prev) => Math.max(1, prev - 1))
                    }}
                    height={buttonSize}
                    width={buttonSize}
                    className="hidden md:flex"
                >
                    <SvgOutlinedChevronRight
                        color={COLORS.black}
                        size={24}
                        style={{ rotate: '180deg' }}
                    />
                </Button>
            )}

            {withButtons && <div className="min-w-2 block md:hidden" />}

            <div
                ref={containerRef}
                className={cn(
                    'flex flex-row items-center md:w-full md:max-w-full scrollbar-hide w-full',
                    withButtons ? 'md:overflow-x-auto' : 'overflow-x-hidden'
                )}
            >
                <div
                    ref={containerScrollRef}
                    className={cn(
                        'w-full flex flex-row items-center justify-start',
                        withButtons ? 'overflow-hidden' : 'overflow-x-auto scrollbar-hide'
                    )}
                    style={{
                        columnGap: `${spacing}px`,
                    }}
                >
                    <InlinePaginationContext.Provider value={{ makeItemRef }}>
                        {renderChildren ? renderChildren(makeItemRef, scrollToRef) : children}
                    </InlinePaginationContext.Provider>
                </div>
            </div>

            {!withButtons && <Gradient className="absolute top-0 right-0" />}

            {withButtons && <div className="min-w-2 block md:hidden" />}

            {withButtons && (
                <Button
                    shape="circle"
                    styleType="tertiary"
                    disabled={page === pagesCount}
                    onClick={() => {
                        setPage((prev) => Math.min(pagesCount, prev + 1))
                    }}
                    height={buttonSize}
                    width={buttonSize}
                    className="hidden md:flex"
                >
                    <SvgOutlinedChevronRight color={COLORS.black} size={24} />
                </Button>
            )}
        </div>
    )
}

interface IPulicInlinePaginationContext {
    makeItemRef: (value: string, isLast: boolean) => (element: HTMLDivElement) => void
}

const InlinePaginationContext = createContext<IPulicInlinePaginationContext>({
    makeItemRef: () => noOp,
})

export const useInlinePaginationContext = () => useContext(InlinePaginationContext)
